import { z } from "zod";
import { LocalizedTextSchema, QuestionRefSchema, VisibilitySchema } from "./common";

// ============================================================================
// PERSISTED TYPES (database storage - UUID references)
// ============================================================================
// These types represent the format stored in the ja-go PostgreSQL database.
//
// API endpoints:
//   - Questions: /v2/support/health-forms/questions/{id}
//   - Books: /v2/support/health-forms/books/{id}
//   - Question Groups: /v2/support/health-forms/question-groups/{id}
//
// Characteristics:
//   - Lightweight storage (avoids duplication)
//   - Parts contain UUID string references
//   - Example: { "_type": "question", "question": "uuid-123" }
// ============================================================================

// QuestionGroup with UUID references (persisted format)
export const QuestionGroupSchema = z.object({
  _type: z.literal("question_group"),
  id: z.number(),
  name: z.string(),
  description: LocalizedTextSchema.optional(),
  questions: z.array(QuestionRefSchema),
  updatedAt: z.string(),
});

export type QuestionGroup = z.infer<typeof QuestionGroupSchema>;

// Part - can reference Question or QuestionGroup
export const PartSchema = z.discriminatedUnion("_type", [
  z
    .object({
      _type: z.literal("question"),
    })
    .merge(QuestionRefSchema),
  z.object({
    _type: z.literal("question_group"),
    visibility: VisibilitySchema,
    questionGroup: z.number(),
  }),
]);

export type Part = z.infer<typeof PartSchema>;

// Page - inline structure within Book
export const PageSchema = z.object({
  title: LocalizedTextSchema.optional(),
  parts: z.array(PartSchema),
});

export type Page = z.infer<typeof PageSchema>;

// Book - root-level form (persisted format)
export const BookSchema = z.object({
  _type: z.literal("book"),
  id: z.number(),
  pages: z.array(PageSchema),
  name: z.string(),
  description: LocalizedTextSchema.optional(),
  footer: LocalizedTextSchema.optional(),
  updatedAt: z.string(),
});

export type Book = z.infer<typeof BookSchema>;

// Validation functions
export const validateQuestionGroup = (data: unknown) => {
  return QuestionGroupSchema.safeParse(data);
};

export const validateBook = (data: unknown) => {
  return BookSchema.safeParse(data);
};
